import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { z } from 'zod'
import {
  getActiveOrganizationId,
  getAnonymousIdFromCookie,
  getEffectiveUser,
} from '@server/identity'
import { USAGE_COUNTER_KEYS } from '@shared/usage-counter-keys'
import { APP_ROUTES, toAppUrl } from '@shared/app-routes'

const loadAuth = async () => (await import('@server/auth')).auth
const loadEnv = async () => (await import('@server/env')).env
const loadUsage = async () => import('@server/usage')
const loadUsageService = async () => import('@server/usage-service')
const loadResourceLimits = async () => import('@server/resource-limits')

export const updateDisplayName = createServerFn({ method: 'POST' })
  .inputValidator(
    z.object({
      name: z
        .string()
        .trim()
        .min(2, 'Display name must be at least 2 characters')
        .max(80, 'Display name must be 80 characters or less'),
    }),
  )
  .handler(async ({ data }) => {
    const request = getRequest()
    const auth = await loadAuth()
    const session = await auth.api.getSession({ headers: request.headers })
    if (!session?.user) {
      throw new Error('Unauthorized')
    }

    await auth.api.updateUser({
      headers: request.headers,
      body: {
        name: data.name,
      },
    })

    return {
      success: true,
      name: data.name,
    }
  })

export const requestEmailChange = createServerFn({ method: 'POST' })
  .inputValidator(
    z.object({
      email: z.string().trim().email('Please enter a valid email address'),
    }),
  )
  .handler(async ({ data }) => {
    const request = getRequest()
    const [auth, env] = await Promise.all([loadAuth(), loadEnv()])
    const session = await auth.api.getSession({ headers: request.headers })
    if (!session?.user) {
      throw new Error('Unauthorized')
    }

    const nextEmail = data.email.toLowerCase()
    if (nextEmail === session.user.email.toLowerCase()) {
      throw new Error('Enter a different email address')
    }

    const callbackURL = toAppUrl(
      APP_ROUTES.dashboard.settings,
      env.BETTER_AUTH_URL,
    )

    await auth.api.changeEmail({
      headers: request.headers,
      body: {
        newEmail: nextEmail,
        callbackURL,
      },
    })

    return {
      success: true,
      message:
        'Confirmation sent. Check your current email address to approve this change.',
    }
  })

/**
 * Upgrades the current user to Pro.
 * @deprecated Use Stripe checkout flow instead.
 */
export const upgradeToPro = createServerFn({ method: 'POST' }).handler(() => {
  throw new Error(
    'upgradeToPro is deprecated. Please use the Stripe checkout flow.',
  )
})

/**
 * Gets the current usage status for the user (or guest).
 * For authenticated and anonymous users, uses the counter-backed limits system.
 */
export const getUserUsageStatus = createServerFn({ method: 'GET' }).handler(
  async () => {
    const request = getRequest()
    const [
      auth,
      { getCurrentUsage },
      { getEffectiveLimits },
      { getCurrentPeriodUsageCount },
    ] = await Promise.all([
      loadAuth(),
      loadUsage(),
      loadResourceLimits(),
      loadUsageService(),
    ])
    const session = await auth.api.getSession({ headers: request.headers })
    const effUser = await getEffectiveUser(
      session?.user ?? null,
      request.headers,
    )

    // Anonymous users also use the counter-backed guest quota.
    if (effUser.type === 'anonymous') {
      return await getCurrentUsage({ ...effUser })
    }

    // For authenticated users, use new resource limits system
    const organizationId = await getActiveOrganizationId(session)
    const limits = await getEffectiveLimits(effUser.id, organizationId)
    const userPlanName = session.user.billingPlan
    const usageResourceType = resolvePromptUsageResourceType(
      userPlanName,
      organizationId,
    )
    const limitValue = resolveUsageLimit(limits, usageResourceType)

    // Get current usage from usage counters.
    const targetType = organizationId ? 'organization' : 'user'
    const targetId = organizationId || effUser.id
    const used = await getCurrentPeriodUsageCount(
      targetId,
      targetType,
      usageResourceType,
    )
    const remaining = limitValue < 0 ? 999999 : Math.max(0, limitValue - used)

    return {
      allowed: limitValue < 0 ? true : remaining > 0,
      count: used,
      limit: limitValue,
      remaining: remaining,
    }
  },
)

function resolvePromptUsageResourceType(
  userPlanName: string | undefined,
  organizationId: string | undefined,
): string {
  if (organizationId) {
    return 'apiUsage'
  }

  if (userPlanName === 'registeredUser') {
    return USAGE_COUNTER_KEYS.PROMPTS_WEEK
  }

  if (userPlanName === 'premiumUser' || userPlanName === 'proUser') {
    return USAGE_COUNTER_KEYS.PROMPTS_MONTH
  }

  return 'apiUsage'
}

function resolveUsageLimit(
  limits: {
    promptLimitWeekly?: number | null
    promptLimitMonthly?: number | null
    apiLimit: number
  },
  usageResourceType: string,
): number {
  if (usageResourceType === USAGE_COUNTER_KEYS.PROMPTS_WEEK) {
    return Number(limits.promptLimitWeekly ?? limits.apiLimit)
  }

  if (usageResourceType === USAGE_COUNTER_KEYS.PROMPTS_MONTH) {
    return typeof limits.promptLimitMonthly === 'number'
      ? limits.promptLimitMonthly
      : -1
  }

  return limits.apiLimit
}

/**
 * Migrates guest history to the logged-in user for the current request.
 */
export async function migrateGuestHistoryForRequest(
  headers: Headers,
  userId: string,
): Promise<{ migrated: boolean; count: number }> {
  const { getGuestHistoryStatus, migrateHistory } = await loadUsage()
  const anonId = await getAnonymousIdFromCookie(headers)
  if (!anonId) return { migrated: false, count: 0 }

  const status = await getGuestHistoryStatus(anonId)
  if (!status.hasHistory) return { migrated: false, count: 0 }

  await migrateHistory(anonId, userId)
  return { migrated: true, count: status.count }
}
